import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Check, ChevronDown, Pin } from '../../../icons';
import {
  Avatar, Button, DropdownMenu, EmptyState, FilterChip, Pagination, SearchInput, Table, type TableColumn,
} from '../../../components/shared';
import {
  listBoardPosts, markBoardRead, searchAllBoards,
  type BoardDto, type BoardPostListItem, type BoardPrefixDto,
} from '../../../api/boards';
import { tBoards, type BoardsI18nKey } from '../../../lib/boards-i18n';
import { isNewPost } from '../../BoardsPage/wide/BoardWideList';
import { highlightMatch } from '../../BoardsPage/wide/highlight';
import { BoardThumb } from '../../BoardsPage/wide/BoardThumb';
import { BoardMark, BoardPrefixPill } from '../BoardMarks';
import { confirmStateOf } from '../confirm-state';
import { BOARD_SORTS, boardPostPath, type BoardListQuery, type BoardSort } from '../board-v1-url';
import { formatListDate } from './board-list-model';
import { makeNavState, type BoardListNavState } from '../post/post-model';

/**
 * 글 목록 — 클래식(표) · 피드(카드) · 검색 결과 · 빈 게시판 (시안 v1 `list-*` · U2).
 *
 * 데이터 경로는 현행 `BoardWideList` 와 **같다** — `listBoardPosts` · `searchAllBoards` · `markBoardRead`.
 * 다른 점은 필터 · 정렬 · 검색어가 **주소에 산다**는 것이다(결정 Q5) — 새로고침 · 뒤로가기 · 링크가 같은 화면을 연다.
 *
 * ★ 서버 값이 아직 없는 칸은 그리지 않는다(계획 §6 U2):
 *   - 표의 **선택 열**(일괄 읽음 · 휴지통)은 서버 요청 R11 전이라 없다.
 *   - 「확인 필요 · 확인함」 **표식**은 2026-09-16 에 붙였다(R1). 다만 시안의 **확인 «열»(`104 / 142`)은
 *     만들지 않는다** — 목록에 분모를 두지 않기로 했다(사용자 결정 ③ · 차이표 §0.25). 배지만 단다.
 *     **피드 카드에는 확인 표식을 안 붙인다**(시안 원문 · 차이표 #12-2).
 *   - 작성자 **부서**는 2026-09-16 에 이었다(서버 R16 — 목록 응답의 `author.department`).
 *     익명 글은 서버가 비워 보내고, 옛 서버는 칸이 없다 → 둘 다 종전과 같은 한 줄이 된다.
 * ★ 검색 중에도 도구 줄은 남긴다 — 치는 도중에 칸이 사라지면 입력이 끊긴다(시안은 검색 결과 상태에서
 *   도구 줄을 숨겼지만, 그 캡처는 머리 검색칸에서 친 경우다).
 * ★ 제목은 `<Link>` 다 — 행 전체를 눌러도 가지만, 키보드로도 갈 수 있어야 한다.
 */

const SORT_LABEL: Record<BoardSort, BoardsI18nKey> = {
  latest: 'boards.wide.sort_latest',
  views: 'boards.wide.sort_views',
  comments: 'boards.wide.sort_comments',
};

export interface BoardPostListCardProps {
  board: BoardDto;
  prefixes: readonly BoardPrefixDto[];
  query: BoardListQuery;
  onQueryChange: (next: Partial<BoardListQuery>, opts?: { replace?: boolean }) => void;
  /**
   * 글 열기. `state` 는 «받아 둔 목록»이다(U3 · 계획 §2.4) — 글 보기의 이전·다음 · 「‹ 목록」이 쓴다.
   * (U3 에서 더한 둘째 인자 — 안 받는 호출부는 그대로 동작한다.)
   */
  onOpenPost: (postId: string, state?: { boardList: BoardListNavState }) => void;
  onWrite: () => void;
  /** 서버가 404 를 주면 — 게시판이 없어졌거나 볼 수 없다. */
  onForbidden: () => void;
  /** 모두 읽음 뒤 — 트리 배지 · KPI 를 다시 센다. */
  onReadChanged: () => void;
}

type ApiError = { response?: { status?: number; data?: { message?: string } }; message?: string };
const errMsg = (e: unknown) => (e as ApiError)?.response?.data?.message ?? (e as ApiError)?.message ?? 'unknown';

export function BoardPostListCard({
  board, prefixes, query, onQueryChange, onOpenPost, onWrite, onForbidden, onReadChanged,
}: BoardPostListCardProps) {
  const [posts, setPosts] = useState<BoardPostListItem[]>([]);
  const [pinned, setPinned] = useState<BoardPostListItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [draft, setDraft] = useState(query.q);

  const forbiddenRef = useRef(onForbidden);
  forbiddenRef.current = onForbidden;

  // 머리 검색칸 · 검색 지우기가 주소를 바꾸면 이 칸도 따라간다.
  useEffect(() => { setDraft(query.q); }, [query.q]);

  // 치는 동안은 주소를 250ms 늦게 바꾼다(주소가 바뀌면 목록을 다시 읽는다).
  useEffect(() => {
    if (draft.trim() === query.q) return undefined;
    const handle = setTimeout(() => onQueryChange({ q: draft.trim() }, { replace: true }), 250);
    return () => clearTimeout(handle);
  }, [draft, query.q, onQueryChange]);

  const term = query.q.trim();
  const searching = term.length > 0;
  const allBoards = searching && query.scope === 'all';

  const fetchPage = useCallback((from: string | null) => (
    allBoards
      ? searchAllBoards(term, from ? { cursor: from } : undefined).then((r) => ({ ...r, pinned: [] as BoardPostListItem[] }))
      : listBoardPosts(board.id, {
        ...(query.prefix ? { prefixId: query.prefix } : {}),
        ...(term ? { q: term } : {}),
        ...(query.sort !== 'latest' ? { sort: query.sort } : {}),
        ...(from ? { cursor: from } : {}),
      })
  ), [allBoards, board.id, query.prefix, query.sort, term]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPage(null)
      .then((r) => {
        if (cancelled) return;
        setPosts(r.posts);
        setPinned(r.pinned ?? []);
        setCursor(r.nextCursor);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        if ((e as ApiError)?.response?.status === 404) { forbiddenRef.current(); return; }
        setPosts([]);
        setPinned([]);
        setCursor(null);
        setError(tBoards('boards.post.err_list', { msg: errMsg(e) }));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fetchPage, attempt]);

  async function loadMore() {
    if (!cursor) return;
    setLoadingMore(true);
    try {
      const r = await fetchPage(cursor);
      setPosts((prev) => [...prev, ...r.posts]);
      setCursor(r.nextCursor);
    } catch (e) {
      setError(tBoards('boards.post.err_list', { msg: errMsg(e) }));
    } finally {
      setLoadingMore(false);
    }
  }

  async function markAllRead() {
    setMarking(true);
    try {
      await markBoardRead(board.id);
      onReadChanged();
      setAttempt((n) => n + 1);
    } catch (e) {
      setError(tBoards('boards.wide.mark_all_read_err', { msg: errMsg(e) }));
    } finally {
      setMarking(false);
    }
  }

  const clearSearch = () => { setDraft(''); onQueryChange({ q: '', scope: 'board' }, { replace: true }); };

  const location = useLocation();
  const navState = useMemo(() => makeNavState(board.id, posts, location.search), [board.id, posts, location.search]);

  const isFeed = board.boardType === 'FEED' && !allBoards;
  const hasUnread = posts.some((p) => !p.isRead) || pinned.some((p) => !p.isRead);
  const views = (p: BoardPostListItem) => (p.locked ? '—' : p.viewCount);
  const meta = (p: BoardPostListItem) => tBoards('boards.post.meta', {
    author: p.author.name, date: formatListDate(p.createdAt), views: views(p),
  });

  /**
   * 확인 요청 표식 (2026-09-16 · R1) — **세 상태 중 「요청 없음」은 아무것도 그리지 않는다.**
   * 회색 배지라도 붙이면 확인과 무관한 글까지 확인 이야기를 하게 된다(시안의 그 행은 `—` 다).
   * ★ **숫자는 붙이지 않는다** — 목록에 분모를 만들지 않기로 했다(사용자 결정 ③ · 차이표 §0.25).
   * ★ 판정은 `confirm-state.ts` 한 곳에서 — 글 보기와 **같은 값**을 본다.
   */
  const confirmMark = (p: BoardPostListItem) => {
    const state = confirmStateOf(p);
    if (state === 'none') return null;
    return <BoardMark kind={state === 'required' ? 'confirm-required' : 'confirmed'} />;
  };

  /**
   * `withConfirm` — 확인 표식을 함께 그릴지.
   * ★ **피드 카드에는 안 그린다**(시안 원문 확인 · 차이표 #12-2). 피드는 커버 카드라
   *   꼬리표 줄이 짧고, 시안도 거기에는 확인 표시를 두지 않았다. 표와 고정 글에만 붙인다.
   */
  const titleMarks = (p: BoardPostListItem, opts?: { withConfirm?: boolean }) => (
    <>
      {p.isPinned && <BoardMark kind="pinned" />}
      {opts?.withConfirm !== false && confirmMark(p)}
      {p.isSecret && <BoardMark kind="secret" />}
      {p.attachmentCount > 0 && <BoardMark kind="attachments" count={p.attachmentCount} />}
      {isNewPost(p.createdAt) && <BoardMark kind="new" />}
    </>
  );

  const columns: TableColumn<BoardPostListItem>[] = [
    { key: 'prefix', header: tBoards('boards.col.prefix'), width: '104px', render: (p) => <BoardPrefixPill label={p.prefix?.label} /> },
    {
      key: 'title',
      header: tBoards('boards.col.title'),
      render: (p) => (
        <div className="bv1-title">
          <div className="bv1-title__line">
            <span className={`bv1-udot${p.isRead ? '' : ' is-on'}`} aria-hidden />
            {!p.isRead && <span className="bv1-sr">{tBoards('boards.wide.unread_dot')}</span>}
            <Link
              to={boardPostPath(p.id)}
              state={navState}
              className="bv1-title__link"
              title={p.title}
              onClick={(e) => e.stopPropagation()}
            >
              {searching ? highlightMatch(p.title, term) : p.title}
            </Link>
            {titleMarks(p)}
          </div>
          {searching && (p.excerpt || (allBoards && p.board)) && (
            <div className="bv1-title__excerpt">
              {allBoards && p.board ? `${p.board.name} · ` : ''}
              {p.excerpt ? highlightMatch(p.excerpt, term) : null}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'author',
      header: tBoards('boards.col.author'),
      width: '168px',
      render: (p) => (
        <span className="bv1-who">
          {!p.author.anonymous && p.author.id && (
            <Avatar userId={Number(p.author.id)} name={p.author.name} src={p.author.avatarUrl ?? null} size="xs" />
          )}
          <span className="bv1-who__box">
            <span className="bv1-who__name">{p.author.name}</span>
            {/*
              부서 — 서버(R16)가 목록 한 장의 작성자를 한 번에 읽어 실어 준다.
              ★ 익명 글은 **서버가 비워서** 보낸다(웹이 감추는 것이 아니다).
              ★ 옛 서버는 이 칸을 안 준다 → 그때는 줄이 아예 안 서고 종전과 같은 한 줄이다.
            */}
            {p.author.department && (
              <span className="bv1-who__dept">{p.author.department}</span>
            )}
          </span>
        </span>
      ),
    },
    { key: 'date', header: tBoards('boards.col.date'), width: '96px', render: (p) => <span className="bv1-num">{formatListDate(p.createdAt)}</span> },
    { key: 'views', header: tBoards('boards.col.views'), width: '64px', numeric: true, render: (p) => views(p) },
    { key: 'comments', header: tBoards('boards.col.comments'), width: '64px', numeric: true, render: (p) => p.commentCount ?? '—' },
  ];

  const sortLabel = tBoards(SORT_LABEL[query.sort]);

  let empty: JSX.Element;
  if (searching) {
    empty = (
      <EmptyState
        kind="no-result"
        title={tBoards('boards.v1.empty.search_title')}
        description={query.scope === 'board' ? tBoards('boards.v1.empty.search_widen') : tBoards('boards.v1.empty.search_change')}
        action={<Button variant="secondary" size="compact" onClick={clearSearch}>{tBoards('boards.wide.search_clear')}</Button>}
      />
    );
  } else if (query.prefix) {
    empty = (
      <EmptyState
        kind="no-result"
        title={tBoards('boards.v1.empty.prefix_title')}
        action={(
          <Button variant="secondary" size="compact" onClick={() => onQueryChange({ prefix: null })}>
            {tBoards('boards.v1.empty.prefix_action')}
          </Button>
        )}
      />
    );
  } else {
    empty = (
      <EmptyState
        kind="first-run"
        title={tBoards('boards.v1.empty.first_title')}
        description={tBoards('boards.v1.empty.first_desc')}
        action={board.canWrite === false ? undefined : (
          <Button variant="primary" size="compact" onClick={onWrite}>{tBoards('boards.v1.empty.first_action')}</Button>
        )}
      />
    );
  }

  return (
    <div className="bv1-list">
      <div className="bv1-tools">
        {!allBoards && (
          <>
            <div className="bv1-tools__chips" role="group" aria-label={tBoards('boards.wide.filter_aria')}>
              <FilterChip pressed={!query.prefix} onClick={() => onQueryChange({ prefix: null })}>
                {tBoards('boards.wide.filter_all')}
              </FilterChip>
              {prefixes.map((p) => (
                <FilterChip key={p.id} pressed={query.prefix === p.id} onClick={() => onQueryChange({ prefix: p.id })}>
                  {p.label}
                </FilterChip>
              ))}
            </div>
            <span className="bv1-tools__sep" aria-hidden />
            <DropdownMenu
              label={tBoards('boards.wide.sort_aria')}
              checkable
              align="start"
              width={160}
              items={BOARD_SORTS.map((s) => ({
                id: s,
                label: tBoards(SORT_LABEL[s]),
                checked: query.sort === s,
                onSelect: () => onQueryChange({ sort: s }),
              }))}
              renderTrigger={(p) => (
                <button
                  type="button"
                  className="bv1-sort"
                  aria-label={`${tBoards('boards.wide.sort_aria')}: ${sortLabel}`}
                  {...p}
                >
                  <span>{sortLabel}</span>
                  <ChevronDown aria-hidden className="bv1-sort__chev" />
                </button>
              )}
            />
          </>
        )}
        {hasUnread && (
          <Button variant="secondary" size="compact" iconLeft={<Check aria-hidden />} disabled={marking} onClick={markAllRead}>
            {tBoards('boards.wide.mark_all_read')}
          </Button>
        )}
        <SearchInput
          wrapperClassName="bv1-tools__search"
          value={draft}
          placeholder={tBoards('boards.v1.list.search_in_board')}
          aria-label={tBoards('boards.v1.list.search_in_board')}
          onChange={(e) => setDraft(e.target.value)}
          onClear={clearSearch}
        />
      </div>

      {searching && (
        <div className="bv1-scope">
          <span className="bv1-scope__text">
            <b>{tBoards('boards.v1.list.search_head', { q: term })}</b>
            {' · '}
            <b className="bv1-num">
              {loading ? '…' : tBoards(cursor ? 'boards.wide.search_more' : 'boards.wide.search_count', { n: posts.length })}
            </b>
            {' · '}
            {tBoards('boards.wide.search_scope')}
          </span>
          <div className="bv1-scope__chips" role="group" aria-label={tBoards('boards.wide.search_scope')}>
            <FilterChip pressed={query.scope === 'board'} onClick={() => onQueryChange({ scope: 'board' })}>
              {tBoards('boards.wide.search_here')}
            </FilterChip>
            <FilterChip pressed={query.scope === 'all'} onClick={() => onQueryChange({ scope: 'all' })}>
              {tBoards('boards.wide.search_all')}
            </FilterChip>
          </div>
          <Button variant="secondary" size="compact" onClick={clearSearch}>{tBoards('boards.wide.search_clear')}</Button>
        </div>
      )}

      {!searching && !query.prefix && pinned.length > 0 && (
        <section className="bv1-pins" aria-label={tBoards('boards.wide.pinned_section')}>
          <div className="bv1-pins__head">
            <Pin aria-hidden className="bv1-pins__icon" />
            {tBoards('boards.wide.pinned_section')}
            <span className="bv1-num">{pinned.length}</span>
          </div>
          {pinned.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`bv1-pins__row${p.isRead ? '' : ' is-unread'}`}
              onClick={() => onOpenPost(p.id, navState)}
            >
              <BoardPrefixPill label={p.prefix?.label} />
              {/* 시안의 고정 글 구획은 「확인 필요」를 **배지로만** 단다 — 숫자는 없다(차이표 #12-1). */}
              {confirmMark(p)}
              <b className="bv1-pins__title" title={p.title}>{p.title}</b>
              <span className="bv1-pins__meta bv1-num">{meta(p)}</span>
            </button>
          ))}
        </section>
      )}

      {error && posts.length === 0 ? (
        <EmptyState
          kind="error"
          title={error}
          action={(
            <Button variant="secondary" size="compact" onClick={() => setAttempt((n) => n + 1)}>
              {tBoards('boards.v1.retry')}
            </Button>
          )}
        />
      ) : !loading && posts.length === 0 ? (
        empty
      ) : isFeed ? (
        <ul className="bv1-feed" aria-busy={loading || undefined}>
          {posts.map((p) => (
            <li key={p.id}>
              <Link to={boardPostPath(p.id)} state={navState} className="bv1-feed__card">
                <span className="bv1-feed__cover">
                  {p.thumbnail
                    ? <BoardThumb fileId={p.thumbnail.fileId} name={p.thumbnail.name} />
                    : <span className="bv1-feed__nocover">{tBoards('boards.v1.list.no_image')}</span>}
                </span>
                <span className="bv1-feed__body">
                  <span className="bv1-feed__tags">
                    {p.prefix && <BoardPrefixPill label={p.prefix.label} />}
                    {titleMarks(p, { withConfirm: false })}
                  </span>
                  <b className="bv1-feed__title">{p.title}</b>
                  <span className="bv1-feed__meta bv1-num">{meta(p)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <Table
          caption={tBoards('boards.v1.list.caption', { name: board.name })}
          columns={columns}
          rows={posts}
          rowKey={(p) => p.id}
          loading={loading}
          onRowClick={(p) => onOpenPost(p.id, navState)}
          rowClassName={(p) => (p.isRead ? undefined : 'bv1-row--unread')}
          wrapClassName="bv1-tablewrap"
        />
      )}

      {posts.length > 0 && (
        <div className="bv1-listfoot">
          <span className="bv1-num">{tBoards('boards.v1.list.shown', { n: posts.length })}</span>
          {cursor && <Pagination mode="more" loading={loadingMore} onMore={() => void loadMore()} />}
        </div>
      )}
    </div>
  );
}
